import type { User } from "firebase/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  DocumentData,
  onSnapshot,
  serverTimestamp,
  setDoc,
  Timestamp,
  Unsubscribe,
} from "firebase/firestore";

import { firestore } from "./firebaseClient";
import {
  EXCHANGE_SOURCES_COLLECTION,
  ExchangeSource,
  RateFieldPath,
} from "../models/exchangeSource";

const mapTimestamp = (value: unknown): Date | null => {
  if (value instanceof Timestamp) {
    return value.toDate();
  }
  return null;
};

const toStringOr = (value: unknown, fallback = ""): string =>
  typeof value === "string" ? value : fallback;
const toNumberOr = (value: unknown, fallback = 0): number =>
  typeof value === "number" ? value : fallback;

const toRateFieldPathOr = (value: unknown): RateFieldPath => {
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value)) {
    return value.filter(
      (token): token is string | number =>
        typeof token === "string" || typeof token === "number"
    );
  }
  return "rate";
};

const toRequestHeadersOr = (
  value: unknown
): Record<string, string> | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const entries = Object.entries(value as Record<string, unknown>);
  const headers: Record<string, string> = {};
  for (const [key, headerValue] of entries) {
    if (typeof key !== "string" || !key.trim()) {
      continue;
    }
    if (typeof headerValue === "string") {
      headers[key] = headerValue;
    } else if (
      typeof headerValue === "number" ||
      typeof headerValue === "boolean"
    ) {
      headers[key] = String(headerValue);
    }
  }
  return Object.keys(headers).length > 0 ? headers : null;
};

const mapExchangeSource = (
  id: string,
  data: Record<string, unknown>
): ExchangeSource => ({
  id,
  name: typeof data.name === "string" ? data.name : null,
  rate: toNumberOr(data.rate, 0),
  sourceUrl: toStringOr(data.sourceUrl),
  fromCurrency: toStringOr(data.fromCurrency),
  toCurrency: toStringOr(data.toCurrency),
  dataStructure: toStringOr(data.dataStructure),
  rateFieldPath: toRateFieldPathOr(data.rateFieldPath),
  requestHeaders: toRequestHeadersOr(data.requestHeaders),
  keywords: Array.isArray(data.keywords)
    ? data.keywords.filter((k): k is string => typeof k === "string")
    : null,
  created: mapTimestamp(data.created),
  updated: mapTimestamp(data.updated),
  lastFetchedAt: mapTimestamp(data.lastFetchedAt),
  schedulerEnabled:
    typeof data.schedulerEnabled === "boolean" ? data.schedulerEnabled : null,
  schedulerFrequencyMinutes:
    toNumberOr(data.schedulerFrequencyMinutes, 0) || null,
  schedulerTimeZone:
    typeof data.schedulerTimeZone === "string" ? data.schedulerTimeZone : null,
  schedulerJobName:
    typeof data.schedulerJobName === "string" ? data.schedulerJobName : null,
  schedulerJobId:
    typeof data.schedulerJobId === "string" ? data.schedulerJobId : null,
  schedulerLocation:
    typeof data.schedulerLocation === "string" ? data.schedulerLocation : null,
  schedulerSchedule:
    typeof data.schedulerSchedule === "string" ? data.schedulerSchedule : null,
  schedulerTargetBaseUrl:
    typeof data.schedulerTargetBaseUrl === "string"
      ? data.schedulerTargetBaseUrl
      : null,
  keyRegenerationUrl:
    typeof data.keyRegenerationUrl === "string" ? data.keyRegenerationUrl : null,
  keyRegenerationMethod:
    typeof data.keyRegenerationMethod === "string"
      ? data.keyRegenerationMethod
      : null,
  keyRegenerationRequestHeaders: toRequestHeadersOr(
    data.keyRegenerationRequestHeaders
  ),
  keyRegenerationRequestBody:
    data.keyRegenerationRequestBody != null &&
    typeof data.keyRegenerationRequestBody === "object" &&
    !Array.isArray(data.keyRegenerationRequestBody)
      ? (data.keyRegenerationRequestBody as Record<string, unknown>)
      : null,
  keyRegenerationResponseFieldPath:
    data.keyRegenerationResponseFieldPath != null
      ? toRateFieldPathOr(data.keyRegenerationResponseFieldPath)
      : null,
  keyRegenerationTargetHeader:
    typeof data.keyRegenerationTargetHeader === "string"
      ? data.keyRegenerationTargetHeader
      : null,
});

export const subscribeToExchangeSources = (
  onNext: (sources: ExchangeSource[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  const ref = collection(firestore, EXCHANGE_SOURCES_COLLECTION);
  return onSnapshot(
    ref,
    (snapshot) => {
      const records = snapshot.docs
        .map((snap) => mapExchangeSource(snap.id, snap.data() as DocumentData))
        .sort((a, b) => {
          const pairLeft = `${a.fromCurrency}:${a.toCurrency}`.toUpperCase();
          const pairRight = `${b.fromCurrency}:${b.toCurrency}`.toUpperCase();
          const pairSort = pairLeft.localeCompare(pairRight);
          if (pairSort !== 0) {
            return pairSort;
          }
          const nameLeft = (a.name ?? "").toUpperCase();
          const nameRight = (b.name ?? "").toUpperCase();
          return nameLeft.localeCompare(nameRight);
        });
      onNext(records);
    },
    (error) => {
      if (onError) {
        onError(error);
      } else {
        console.error("Exchange sources subscription error:", error);
      }
    }
  );
};

export const createExchangeSource = async (input: {
  name?: string;
  rate: number;
  sourceUrl: string;
  fromCurrency: string;
  toCurrency: string;
  dataStructure: string;
  rateFieldPath: RateFieldPath;
  requestHeaders?: Record<string, string> | null;
  keyRegenerationUrl?: string | null;
  keyRegenerationMethod?: string | null;
  keyRegenerationRequestHeaders?: Record<string, string> | null;
  keyRegenerationRequestBody?: Record<string, unknown> | null;
  keyRegenerationResponseFieldPath?: RateFieldPath | null;
  keyRegenerationTargetHeader?: string | null;
}): Promise<string> => {
  const docRef = await addDoc(
    collection(firestore, EXCHANGE_SOURCES_COLLECTION),
    {
      name: input.name ?? "",
      rate: input.rate,
      sourceUrl: input.sourceUrl,
      fromCurrency: input.fromCurrency,
      toCurrency: input.toCurrency,
      dataStructure: input.dataStructure,
      rateFieldPath: input.rateFieldPath,
      requestHeaders: input.requestHeaders ?? null,
      keyRegenerationUrl: input.keyRegenerationUrl ?? null,
      keyRegenerationMethod: input.keyRegenerationMethod ?? null,
      keyRegenerationRequestHeaders: input.keyRegenerationRequestHeaders ?? null,
      keyRegenerationRequestBody: input.keyRegenerationRequestBody ?? null,
      keyRegenerationResponseFieldPath:
        input.keyRegenerationResponseFieldPath ?? null,
      keyRegenerationTargetHeader: input.keyRegenerationTargetHeader ?? null,
      created: serverTimestamp(),
      updated: serverTimestamp(),
    }
  );
  return docRef.id;
};

export const updateExchangeSource = async (
  id: string,
  patch: Partial<{
    name: string;
    rate: number;
    sourceUrl: string;
    fromCurrency: string;
    toCurrency: string;
    dataStructure: string;
    rateFieldPath: RateFieldPath;
    requestHeaders: Record<string, string> | null;
    keyRegenerationUrl: string | null;
    keyRegenerationMethod: string | null;
    keyRegenerationRequestHeaders: Record<string, string> | null;
    keyRegenerationRequestBody: Record<string, unknown> | null;
  keyRegenerationResponseFieldPath: RateFieldPath | null;
  keyRegenerationTargetHeader: string | null;
  }>
): Promise<void> => {
  const ref = doc(firestore, EXCHANGE_SOURCES_COLLECTION, id);
  await setDoc(
    ref,
    {
      ...patch,
      updated: serverTimestamp(),
    },
    { merge: true }
  );
};

export const deleteExchangeSource = async (id: string): Promise<void> => {
  await deleteDoc(doc(firestore, EXCHANGE_SOURCES_COLLECTION, id));
};

const getBackendBaseUrl = (): string => {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (!raw) {
    throw new Error("Missing REACT_APP_BACKEND_URL environment variable.");
  }
  return raw.replace(/\/+$/, "");
};

export const refreshExchangeSourceRate = async (
  user: User,
  exchangeSourceId: string
): Promise<{
  exchangeSourceId: string;
  oldRate: number | null;
  newRate: number;
}> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();

  const response = await fetch(
    `${baseUrl}/api/v1/exchange-sources/${encodeURIComponent(
      exchangeSourceId
    )}/refresh`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const data = (await response.json().catch(() => null)) as Record<
    string,
    unknown
  > | null;

  if (!response.ok) {
    const detail =
      data && typeof data.detail === "string"
        ? data.detail
        : `Backend error (${response.status}).`;
    throw new Error(detail);
  }

  return {
    exchangeSourceId: String(data?.exchangeSourceId ?? exchangeSourceId),
    oldRate:
      typeof data?.oldRate === "number"
        ? data.oldRate
        : data?.oldRate == null
        ? null
        : null,
    newRate: Number(data?.newRate ?? 0),
  };
};

export const upsertExchangeSourceSchedulerJob = async (
  user: User,
  exchangeSourceId: string,
  input: { frequencyMinutes: number; timeZone: string }
): Promise<void> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();

  const response = await fetch(
    `${baseUrl}/api/v1/exchange-sources/${encodeURIComponent(
      exchangeSourceId
    )}/scheduler-job`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        frequencyMinutes: input.frequencyMinutes,
        timeZone: input.timeZone,
        targetBaseUrl: baseUrl,
      }),
    }
  );

  const data = (await response.json().catch(() => null)) as Record<
    string,
    unknown
  > | null;

  if (!response.ok) {
    const detail =
      data && typeof data.detail === "string"
        ? data.detail
        : `Backend error (${response.status}).`;
    throw new Error(detail);
  }
};

export const deleteExchangeSourceSchedulerJob = async (
  user: User,
  exchangeSourceId: string
): Promise<void> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();

  const response = await fetch(
    `${baseUrl}/api/v1/exchange-sources/${encodeURIComponent(
      exchangeSourceId
    )}/scheduler-job`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const data = (await response.json().catch(() => null)) as Record<
    string,
    unknown
  > | null;

  if (!response.ok) {
    const detail =
      data && typeof data.detail === "string"
        ? data.detail
        : `Backend error (${response.status}).`;
    throw new Error(detail);
  }
};

export const resolveExchangeSourceRate = async (input: {
  sourceUrl: string;
  rateFieldPath: RateFieldPath;
  requestHeaders?: Record<string, string> | null;
}): Promise<{
  rate: number;
  rawValue: unknown;
  rateFieldTokens: Array<string | number>;
}> => {
  const baseUrl = getBackendBaseUrl();

  const response = await fetch(`${baseUrl}/api/v1/exchange-sources/resolve`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      sourceUrl: input.sourceUrl,
      rateFieldPath: input.rateFieldPath,
      requestHeaders: input.requestHeaders ?? null,
    }),
  });

  const data = (await response.json().catch(() => null)) as Record<
    string,
    unknown
  > | null;

  if (!response.ok) {
    const detail =
      data && typeof data.detail === "string"
        ? data.detail
        : `Backend error (${response.status}).`;
    throw new Error(detail);
  }

  return {
    rate: Number(data?.rate ?? 0),
    rawValue: data?.rawValue,
    rateFieldTokens: Array.isArray(data?.rateFieldTokens)
      ? (data?.rateFieldTokens as Array<string | number>)
      : [],
  };
};
